import { createAction } from '@reduxjs/toolkit';
import {
  AudioEditSlice,
  AudioEditDetails,
  TranscriptionViewableWords,
  RedactedWords,
} from '../models';
import { IndividualRedactionCode } from '@redact-modules/redactionCodes/models';
export const RESET_WORDS_LOG = createAction<ResetWordsRequest>(
  'vtn-component-transcription/RESET_WORDS_LOG'
);
export const actionResetWordsLog = (payload: ResetWordsRequest) =>
  RESET_WORDS_LOG(payload);

export const SELECT_WORDS = createAction<SelectWordsRequest>(
  'vtn-component-transcription/SELECT_WORDS'
);
export type SelectWordsRequest = ReadonlyArray<TranscriptionViewableWords>;
export const actionSelectWords = (payload: SelectWordsRequest) =>
  SELECT_WORDS(payload);

export const SELECT_RANGE_WORDS = createAction<SelectRangeWordsRequest>(
  'vtn-component-transcription/SELECT_RANGE_WORDS'
);
export type SelectRangeWordsRequest = [
  TranscriptionViewableWords,
  TranscriptionViewableWords,
];
export const actionRangeSelectWords = (payload: SelectRangeWordsRequest) =>
  SELECT_RANGE_WORDS(payload);

export const DESELECT_WORDS = createAction<DeselectWordsRequest>(
  'vtn-component-transcription/DESELECT_WORDS'
);
export type DeselectWordsRequest = ReadonlyArray<TranscriptionViewableWords>;
export const actionDeselectWords = (payload: DeselectWordsRequest) =>
  DESELECT_WORDS(payload);

export const DESELECT_ALL = createAction<DeselectAllRequest>(
  'vtn-component-transcription/DESELECT_ALL'
);
export type DeselectAllRequest = void;
export const actionDeselectAll = (payload: DeselectAllRequest) =>
  DESELECT_ALL(payload);

export const REDACT_WORDS = createAction<RedactWordsRequest>(
  'vtn-component-transcription/REDACT_WORDS'
);
export type RedactWordsRequest = ReadonlyArray<TranscriptionViewableWords>;
export const actionRedactWords = (payload: RedactWordsRequest) =>
  REDACT_WORDS(payload);

export const MASK_VOICE_WORDS = createAction<MaskVoiceWordsRequest>(
  'vtn-component-transcription/MASK_VOICE_WORDS'
);
export type MaskVoiceWordsRequest = ReadonlyArray<TranscriptionViewableWords>;
export const actionMaskVoiceWords = (payload: MaskVoiceWordsRequest) =>
  MASK_VOICE_WORDS(payload);

export const RESET_WORDS = createAction<ResetWordsRequest>(
  'vtn-component-transcription/RESET_WORDS'
);
export type ResetWordsRequest = ReadonlyArray<TranscriptionViewableWords>;
export const actionResetWords = (payload: ResetWordsRequest) =>
  RESET_WORDS(payload);

export const REDACT_SLICE = createAction<RedactSliceRequest>(
  'vtn-component-transcription/REDACT_SLICE'
);
export interface RedactSliceRequest {
  redact: Array<AudioEditSlice>;
  reset?: Array<AudioEditSlice>;
}
export const actionRedactSlice = (payload: RedactSliceRequest) =>
  REDACT_SLICE(payload);

export const MASK_SLICE = createAction<MaskSliceRequest>(
  'vtn-component-transcription/MASK_SLICE'
);
export interface MaskSliceRequest {
  mask: Array<AudioEditSlice>;
  reset?: Array<AudioEditSlice>;
}
export const actionMaskSlice = (payload: MaskSliceRequest) =>
  MASK_SLICE(payload);

export const RESET_SLICE = createAction<ResetSliceRequest>(
  'vtn-component-transcription/RESET_SLICE'
);
export type ResetSliceRequest = Array<AudioEditSlice>;
export const actionResetSlice = (payload: ResetSliceRequest) =>
  RESET_SLICE(payload);

export const SELECT_SLICE = createAction<SelectSliceRequest>(
  'vtn-component-transcription/SELECT_SLICE'
);
export type SelectSliceRequest = AudioEditSlice;
export const actionSelectSlice = (payload: SelectSliceRequest) =>
  SELECT_SLICE(payload);

// Note this action is not used anywhere currently!
export const REMOVE_ALL_AUDIO_REDACTIONS = createAction(
  'vtn-component-transcription/REMOVE_ALL_AUDIO_REDACTIONS'
);
export type RemoveAllAudioRedactionsRequest = void;
export const actionRemoveAllAudioRedactions = () =>
  REMOVE_ALL_AUDIO_REDACTIONS();

export const REMOVE_ALL_VOICE_MASK_EDITS = createAction(
  'vtn-component-transcription/REMOVE_ALL_VOICE_MASK_EDITS'
);
export type RemoveAllVoiceMaskEditsRequest = void;
export const actionRemoveAllVoiceMaskEdits = () =>
  REMOVE_ALL_VOICE_MASK_EDITS();

export const DELETE_TRANSCRIPTION_RESULTS = createAction<{
  removeAudioRedactions?: boolean;
  removeVoiceMask?: boolean;
}>('vtn-component-transcription/DELETE_TRANSCRIPTION_RESULTS');
export const actionDeleteTranscriptionResults = (payload: {
  removeAudioRedactions?: boolean;
  removeVoiceMask?: boolean;
}) => DELETE_TRANSCRIPTION_RESULTS(payload);
export const DELETE_TRANSCRIPTION_RESULTS_SUCCESS =
  'DELETE_TRANSCRIPTION_RESULTS_SUCCESS';
export const DELETE_TRANSCRIPTION_RESULTS_FAILURE =
  'DELETE_TRANSCRIPTION_RESULTS_FAILURE';

export const APPLY_DELETE_TRANSCRIPTION = createAction<{
  removeAudioRedactions?: boolean;
  removeVoiceMask?: boolean;
}>('vtn-component-transcription/APPLY_DELETE_TRANSCRIPTION');
export const actionApplyDeleteTranscription = (payload: {
  removeAudioRedactions?: boolean;
  removeVoiceMask?: boolean;
}) => APPLY_DELETE_TRANSCRIPTION(payload);

// Speaker separation alone: drops the speaker grouping and leaves the
// transcript, which is also the only way to run the engine a second time —
// the Run option hides itself once a separated transcript exists.
export const DELETE_SPEAKER_SEPARATION_RESULTS = createAction(
  'vtn-component-transcription/DELETE_SPEAKER_SEPARATION_RESULTS'
);
export const actionDeleteSpeakerSeparationResults = () =>
  DELETE_SPEAKER_SEPARATION_RESULTS();
export const DELETE_SPEAKER_SEPARATION_RESULTS_SUCCESS =
  'DELETE_SPEAKER_SEPARATION_RESULTS_SUCCESS';
export const DELETE_SPEAKER_SEPARATION_RESULTS_FAILURE =
  'DELETE_SPEAKER_SEPARATION_RESULTS_FAILURE';

export const APPLY_DELETE_SPEAKER_SEPARATION = createAction(
  'vtn-component-transcription/APPLY_DELETE_SPEAKER_SEPARATION'
);
export const actionApplyDeleteSpeakerSeparation = () =>
  APPLY_DELETE_SPEAKER_SEPARATION();

export const VIEW_NOTES = createAction<ViewNotesRequest>(
  'vtn-component-transcription/VIEW_NOTES'
);
export type ViewNotesRequest = AudioEditSlice;
export const actionViewNotes = (payload: ViewNotesRequest) =>
  VIEW_NOTES(payload);

export const SET_CURRENT_WORDS_FOR_NOTES = createAction<string>(
  'vtn-component-transcription/SET_CURRENT_WORDS_FOR_NOTES'
);
export const actionSetCurrentWordsForNotes = (payload: string) =>
  SET_CURRENT_WORDS_FOR_NOTES(payload);

export const CLOSE_NOTES = createAction(
  'vtn-component-transcription/CLOSE_NOTES'
);
export const actionCloseNotes = () => CLOSE_NOTES();

export const UPDATE_NOTES = createAction<UpdateNotesRequest>(
  'vtn-component-transcription/UPDATE_NOTES'
);
export type UpdateNotesRequest = AudioEditSlice & {
  2: AudioEditDetails;
};
export const actionUpdateNotes = (payload: UpdateNotesRequest) =>
  UPDATE_NOTES(payload);

export const DELETE_NOTES = createAction<DeleteNotesRequest>(
  'vtn-component-transcription/DELETE_NOTES'
);
export type DeleteNotesRequest = AudioEditSlice;
export const actionDeleteNotes = (payload: DeleteNotesRequest) =>
  DELETE_NOTES(payload);

/** audio buffer - similar actions to audio notes  */
export const VIEW_BUFFER = createAction<ViewBufferRequest>(
  'vtn-component-transcription/VIEW_BUFFER'
);

export type ViewBufferRequest = RedactedWords; // rname to LoadBufferRequest?

export const actionViewBuffer = (payload: ViewBufferRequest) =>
  VIEW_BUFFER(payload);

export const CLOSE_BUFFER = createAction(
  'vtn-component-transcription/CLOSE_BUFFER'
);
export const actionCloseBuffer = () => CLOSE_BUFFER();

export const UPDATE_BUFFER = createAction<UpdateBufferRequest>(
  'vtn-component-transcription/UPDATE_BUFFER'
);

export interface UpdateAudioEditRequest {
  oldSlice: AudioEditSlice;
  newSlice: AudioEditSlice & {
    2: AudioEditDetails;
  };
}

export type UpdateBufferRequest = UpdateAudioEditRequest;

export const actionUpdateBuffer = (payload: UpdateBufferRequest) =>
  UPDATE_BUFFER(payload);

export const DELETE_BUFFER = createAction<DeleteBufferRequest>(
  'vtn-component-transcription/DELETE_BUFFER'
);
export type DeleteBufferRequest = AudioEditSlice & {
  2: AudioEditDetails;
};

export const actionDeleteBuffers = (payload: DeleteBufferRequest) =>
  DELETE_BUFFER(payload);

export const ADD_REDACTION_CODE_TO_TRANSCRIPTION =
  createAction<AddRedactionCodeToTranscriptionRequest>(
    'vtn-component-transcription/ADD_REDACTION_CODE_TO_TRANSCRIPTION'
  );
export interface AddRedactionCodeToTranscriptionRequest {
  word: RedactedWords;
  redactionCode: IndividualRedactionCode;
}
export const actionAddRedactionCodeToTranscription = (
  payload: AddRedactionCodeToTranscriptionRequest
) => ADD_REDACTION_CODE_TO_TRANSCRIPTION(payload);

export const REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION =
  createAction<RemoveRedactionCodeFromTranscriptionRequest>(
    'vtn-component-transcription/REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION'
  );
export type RemoveRedactionCodeFromTranscriptionRequest =
  TranscriptionViewableWords;
export const actionRemoveRedactionCodeFromTranscription = (
  payload: RemoveRedactionCodeFromTranscriptionRequest
) => REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION(payload);
